import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Layers, PenTool, Share2, Play, Pencil, VideoIcon } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Video } from '../types/video';
import { useVideos } from '../hooks/useVideos';
import './RecordingStudioPage.css';

type StudioCardConfig = {
  id: string;
  accentClass: string;
  icon: LucideIcon;
  titleKey: string;
  descKey: string;
};

interface RecordingStudioPageProps {
  /** When provided, shows the "Edit a video" picker section below the feature cards. */
  onSelectVideo?: (video: Video) => void;
}

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://nodebackendforacvrecording-1.onrender.com';

function toAbsoluteUrl(value?: string): string {
  if (!value || typeof value !== 'string') return '';
  if (/^https?:\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) return value;
  if (value.startsWith('//')) return `${window.location.protocol}${value}`;
  const base = API_BASE_URL.replace(/\/+$/, '');
  return `${base}${value.startsWith('/') ? value : `/${value}`}`;
}

/**
 * Landing page for Recording Studio.
 * Shows product feature cards (implemented capabilities) + optional video picker for the editor.
 * Preserved as-is when onSelectVideo is not provided (original placeholder behaviour).
 */
export default function RecordingStudioPage({ onSelectVideo }: RecordingStudioPageProps) {
  const { t } = useTranslation();
  const { videos, loading } = useVideos();

  const cards = useMemo<StudioCardConfig[]>(
    () => [
      {
        id: 'timeline',
        accentClass: 'accent-1',
        icon: Layers,
        titleKey: 'recording_studio.cards.timeline.title',
        descKey: 'recording_studio.cards.timeline.description',
      },
      {
        id: 'annotations',
        accentClass: 'accent-2',
        icon: PenTool,
        titleKey: 'recording_studio.cards.annotations.title',
        descKey: 'recording_studio.cards.annotations.description',
      },
      {
        id: 'publish',
        accentClass: 'accent-3',
        icon: Share2,
        titleKey: 'recording_studio.cards.publish.title',
        descKey: 'recording_studio.cards.publish.description',
      },
    ],
    [],
  );

  return (
    <div className="airec-rs-empty-state">
      <nav
        className="airec-rs-empty-breadcrumb airec-rs-feb-breadcrumb"
        aria-label={t('recording_studio.breadcrumb_aria')}
      >
        <span>{t('acv_recording')}</span>
        <ChevronRight size={12} aria-hidden />
        <span className="is-active">{t('recording_studio.breadcrumb_current')}</span>
      </nav>

      <p className="airec-rs-empty-subtitle">{t('recording_studio.subtitle')}</p>

      <div className="airec-rs-cards-grid">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <article
              key={card.id}
              className={`airec-rs-empty-card ${card.accentClass}`}
              aria-labelledby={`airec-rs-empty-card-title-${card.id}`}
            >
              <span className="airec-rs-lesson-card-emblem" aria-hidden>
                <Icon size={22} strokeWidth={2.2} />
              </span>
              <div className="airec-rs-card-content">
                <h3 id={`airec-rs-empty-card-title-${card.id}`} className="airec-rs-empty-card-title">
                  {t(card.titleKey)}
                </h3>
                <p className="airec-rs-empty-card-description">{t(card.descKey)}</p>
              </div>
            </article>
          );
        })}
      </div>

      {/* ── Video picker — only rendered when editor is wired ── */}
      {onSelectVideo && (
        <section className="airec-rs-picker" aria-labelledby="airec-rs-picker-title">
          <h2 id="airec-rs-picker-title" className="airec-rs-picker-title">
            <Pencil size={15} aria-hidden />
            {t('recording_studio.picker_title')}
          </h2>

          {loading ? (
            <div className="airec-rs-picker-grid">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="airec-rs-picker-skeleton" aria-hidden />
              ))}
            </div>
          ) : videos.length === 0 ? (
            <div className="airec-rs-picker-empty">
              <VideoIcon size={32} aria-hidden />
              <span>{t('recording_studio.picker_empty')}</span>
            </div>
          ) : (
            <div className="airec-rs-picker-grid">
              {videos.map((video) => {
                const thumb = toAbsoluteUrl(video.thumbnailUrl || video.thumbnail_url || '');
                const src = toAbsoluteUrl(video.streamUrl || video.url || video.file_url || '');
                return (
                  <button
                    key={video.id}
                    className="airec-rs-picker-card"
                    onClick={() => onSelectVideo(video)}
                    aria-label={t('recording_studio.picker_aria_edit', { title: video.title ?? '' })}
                  >
                    <div className="airec-rs-picker-thumb">
                      {thumb ? (
                        <img src={thumb} alt="" aria-hidden />
                      ) : src ? (
                        <video src={src} preload="metadata" muted playsInline aria-hidden />
                      ) : (
                        <div className="airec-rs-picker-thumb-placeholder" aria-hidden>
                          <Play size={22} />
                        </div>
                      )}
                      <div className="airec-rs-picker-overlay" aria-hidden>
                        <Pencil size={18} />
                        <span>{t('recording_studio.picker_edit_overlay')}</span>
                      </div>
                    </div>
                    <p className="airec-rs-picker-label">{video.title}</p>
                  </button>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
